/**
 * SOLID Review
 * S — Strong. One concern: the Application's lollipop for the server's events — one op, one union.
 * O — Good. A new event is one more arm of the union; the op does not change.
 * L — N/A.
 * I — Strong. One op.
 * D — Good. The kernel's zod adapter and the Domain's event schemas.
 */
import { z } from 'zod';
import { zodPortContract, type PortInterface, type IConfigZodPortContract } from '@ml3d/kernel/zod';
import { ContextRequest } from '../../domain/ports/schemas/ContextRequest';
import { ResultVoid } from '../../domain/ports/schemas/ResultSchema';
import {
  PayloadRoundStarted, PayloadClaimAnnounced, PayloadCupLifted, PayloadDiceRolled, PayloadRoomRoster, PayloadPresence,
  PayloadRoomAbandoned, PayloadDecisionCountdown, PayloadTableView, PayloadConnectivity,
} from '../../domain/ports/schemas/TableEvents';
import { PayloadConsentState } from '../../domain/ports/schemas/ConsentMoves';
import { PayloadCreditsUpdated } from '../../domain/ports/schemas/PaymentMoves';

/**
 * THE SERVER'S EVENTS, AS COMMANDS (frontend strict-ports plan, FE1, 2026-10-03). The frontend has no
 * stream port: Infrastructure's subscriptions and its websocket monitor DELIVER each event to this
 * lollipop, one request/reply call per event — the shape of the backend's `OutboxConsumer` and
 * `TimerCommands`. Every seam stays request/reply and transportable, so the kernel needs no change, and
 * the Application decides what each event does to the table. Before, a callback in whichever component
 * happened to be mounted decided it.
 */
const ClientServerEvent = z.discriminatedUnion('type', [
  z.object({ type: z.literal('roundStarted'), payload: PayloadRoundStarted }),
  z.object({ type: z.literal('claimAnnounced'), payload: PayloadClaimAnnounced }),
  z.object({ type: z.literal('cupLifted'), payload: PayloadCupLifted }),
  z.object({ type: z.literal('diceRolled'), payload: PayloadDiceRolled }),
  z.object({ type: z.literal('roomUpdated'), payload: PayloadRoomRoster }),
  z.object({ type: z.literal('playerSuspended'), payload: PayloadPresence }),
  z.object({ type: z.literal('playerResumed'), payload: PayloadPresence }),
  z.object({ type: z.literal('roomAbandoned'), payload: PayloadRoomAbandoned }),
  z.object({ type: z.literal('decisionCountdown'), payload: PayloadDecisionCountdown }),
  z.object({ type: z.literal('tableView'), payload: PayloadTableView }),
  z.object({ type: z.literal('connectivity'), payload: PayloadConnectivity }),
  // FE4b: the consent platform's changes arrive the same way — an event from outside the client core,
  // delivered, not streamed (the plan's "no stream port").
  z.object({ type: z.literal('consentChanged'), payload: PayloadConsentState }),
  // FE4c: a player's balance after a game's charge, pushed on their own topic (backend §28.190).
  z.object({ type: z.literal('creditsUpdated'), payload: PayloadCreditsUpdated }),
]).meta({ id: 'ClientServerEvent' });

const ClientServerEventsOps = {
  deliver: { args: z.tuple([ContextRequest, ClientServerEvent]), result: ResultVoid },
} as const;
const clientServerEventsConfig: IConfigZodPortContract<typeof ClientServerEventsOps> = { name: 'ClientServerEvents', ops: ClientServerEventsOps };
export const ClientServerEventsContract = zodPortContract(clientServerEventsConfig);

export type IPortClientServerEvents = PortInterface<typeof ClientServerEventsOps>;
export type IPayloadClientServerEvent = z.infer<typeof ClientServerEvent>;
/**
 * The table's events — every delivery but the consent platform's (FE4b), which the legal side takes, and
 * the account's credits (FE4c), which the payment side takes.
 */
export type TTableEvent = Exclude<IPayloadClientServerEvent, { type: 'consentChanged' | 'creditsUpdated' }>;
